import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  getCart,
  addItemToCart,
  deleteItemFromCart,
  addItemsToCart,
  deleteVerificationFromCart,
} from './thunks';
import { CartItem, FetchStatusType, Item } from '../../../types/interfaces';

export interface CartState {
  status: FetchStatusType;
  error: string | null;
  currency: string;
  items: Item[];
  amount_previous_order: number;
  loadingId: number | null;
}

const initialState: CartState = {
  status: 'idle',
  currency: '',
  error: null,
  amount_previous_order: 0,
  items: [],
  loadingId: null,
};

function mapCartItems(items: CartItem[]): Item[] {
  return items.map((item: CartItem) => ({
    ...item,
    ...item.item,
  }));
}

function setPending(state: CartState, loadingId: number | null = null) {
  state.status = 'loading';
  state.error = null;
  state.loadingId = loadingId;
}

function setFulfilled(state: CartState, items: CartItem[]) {
  state.status = 'succeeded';
  state.loadingId = null;
  state.items = mapCartItems(items);
}

function setRejected(state: CartState, message: string) {
  state.status = 'failed';
  state.loadingId = null;
  state.error = message;
}

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    resetCart: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCart.pending, (state) => {
        setPending(state);
      })
      .addCase(getCart.fulfilled, (state, action) => {
        setFulfilled(state, action.payload.items);
        if (action.payload.currency) {
          state.currency = action.payload.currency;
        }
        if (action.payload.amount_previous_order) {
          state.amount_previous_order = action.payload.amount_previous_order;
        }
      })
      .addCase(getCart.rejected, (state, action) => {
        setRejected(state, action.error.message || 'Failed to fetch cart');
      })

      .addCase(addItemToCart.pending, (state, action) => {
        setPending(state, action.meta.arg.id);
      })
      .addCase(addItemToCart.fulfilled, (state, action) => {
        setFulfilled(state, action.payload.items);
      })
      .addCase(addItemToCart.rejected, (state, action) => {
        setRejected(state, action.error.message || 'Failed to add item to cart');
      })

      .addCase(addItemsToCart.pending, (state) => {
        setPending(state);
      })
      .addCase(addItemsToCart.fulfilled, (state, action) => {
        setFulfilled(state, action.payload.items);
      })
      .addCase(addItemsToCart.rejected, (state, action) => {
        setRejected(state, action.error.message || 'Failed to add items to cart');
      })

      .addCase(deleteItemFromCart.pending, (state, action) => {
        setPending(state, action.meta.arg);
      })
      .addCase(deleteItemFromCart.fulfilled, (state, action) => {
        setFulfilled(state, action.payload.items);
      })
      .addCase(deleteItemFromCart.rejected, (state, action) => {
        setRejected(state, action.error.message || 'Failed to delete item from cart');
      })

      .addCase(deleteVerificationFromCart.pending, (state, action) => {
        setPending(state, action.meta.arg.id);
      })
      .addCase(deleteVerificationFromCart.fulfilled, (state, action) => {
        setFulfilled(state, action.payload.items);
      })
      .addCase(deleteVerificationFromCart.rejected, (state, action) => {
        setRejected(state, action.error.message || 'Failed to delete verification from cart');
      });
  },
});

export const { resetCart } = cartSlice.actions;

export default cartSlice.reducer;
